import { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  RichTextEditor,
  RichTextEditorRef,
} from "@/components/ui/rich-text-editor";
import CompactFileUpload from "@/components/ui/compact-file-upload";
import { EnhancedComment } from "./EnhancedComment";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Timeline,
  TimelineItem,
  TimelineIcon,
  TimelineContent,
  TimelineTime,
} from "@/components/ui/timeline";
import {
  MessageSquare,
  Send,
  CheckCircle,
  Clock,
  Folder,
  PauseCircle,
  XCircle,
  UserCircle,
  Users,
  FileText,
  Trash2,
  Activity,
  Settings,
  UserPlus,
  UserMinus,
  Paperclip,
  Search,
  Filter,
  X,
  Link,
  Copy,
  Minus,
  Plus,
  ClipboardCheck,
  RotateCcw,
  Reply,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { projectApi } from "@/services/projectService";
import { ProjectStatus } from "@/enums/project.enum";
import { Roles } from "@/enums/roles.enum";
import { cn } from "@/lib/utils";
import {
  GetProjectResponseDto,
  SubmissionResponseDto,
} from "@/dto/get-project-response.dto";
import { CommentData } from "@/dto/add-comment-response.dto";

interface AttachmentFile extends File {
  id: string;
  preview?: string;
}

// Detailed change interface for submission/project updates
interface DetailedChange {
  field: string;
  oldValue: string;
  newValue: string;
  label: string;
}

// Standardized meta for all status history entries
interface TimelineMeta {
  action: string;
  memberName?: string;
  role?: string;
  commentId?: string;
  name?: string;
  commentData?: CommentData; // For actual comment content in timeline
  changesSummary?: string;
  detailedChanges?: DetailedChange[]; // Detailed changes with old/new values
  [key: string]: unknown;
}

// Enhanced Timeline Entry Interface
interface TimelineEntry {
  _id: string;
  type:
    | "creation"
    | "status"
    | "comment"
    | "member"
    | "submission"
    | "project_update";
  timestamp: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  status?: ProjectStatus;
  prevStatus?: ProjectStatus;
  content?: string | React.ReactElement; // Can be string or React element for comments
  meta?: TimelineMeta;
  replies?: CommentData[]; // Nested replies for system events
}

interface ProjectTimelineTabProps {
  project: GetProjectResponseDto;
  submission: SubmissionResponseDto;
  statusHistory?: GetProjectResponseDto["statusHistory"];
}

// Enhanced status icons with better variety
const statusIcons = {
  [ProjectStatus.PENDING]: Clock,
  [ProjectStatus.SUBMITTED]: Send,
  [ProjectStatus.IN_PROGRESS]: Activity,
  [ProjectStatus.ON_HOLD]: PauseCircle,
  [ProjectStatus.COMPLETED]: CheckCircle,
  [ProjectStatus.REJECTED]: XCircle,
  [ProjectStatus.DRAFT]: FileText,
};

// Action icons for different timeline events
const actionIcons = {
  creation: UserCircle,
  comment_added: MessageSquare,
  comment_deleted: Trash2,
  member_added: UserPlus,
  member_removed: UserMinus,
  member_joined: Users,
  member_left: UserMinus,
  submission_created: FileText,
  submission_edited: Settings,
  submission_restored: RotateCcw,
  status_change: Activity,
  project_updated: Settings,
};

const ProjectTimelineTab = ({
  project,
  submission,
  statusHistory = [],
}: ProjectTimelineTabProps) => {
  const { user } = useAuth();
  const { addComment, editComment, removeComment } = useProjects();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for restore confirmation dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{
    entryId: string;
    changeCount: number;
    fieldNames?: string[]; // Optional field names for individual field restoration
  } | null>(null);

  // State for inline replies to system events
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [inlineReplyContent, setInlineReplyContent] = useState("");
  const [inlineReplyRichContent, setInlineReplyRichContent] = useState("");
  const [inlineReplyAttachments, setInlineReplyAttachments] = useState<File[]>(
    []
  );
  const [inlineReplyMentions, setInlineReplyMentions] = useState<string[]>([]);
  const inlineReplyEditorRef = useRef<RichTextEditorRef>(null);

  // Mutation for restoring submission
  const restoreMutation = useMutation({
    mutationFn: ({
      statusHistoryId,
      fieldsToRestore,
    }: {
      statusHistoryId: string;
      fieldsToRestore?: string[];
    }) =>
      projectApi.restoreSubmission(
        project._id,
        statusHistoryId,
        fieldsToRestore
      ),
    onSuccess: (data) => {
      toast({
        title: "Submission restored",
        description: `Successfully restored ${data.restoredFields} field(s) to previous values`,
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["projects", project._id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setRestoreDialogOpen(false);
      setRestoreTarget(null);
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { error?: string } } })?.response
              ?.data?.error || "Failed to restore submission";
      toast({
        title: "Restore failed",
        description: errorMessage,
        variant: "destructive",
      });
      setRestoreDialogOpen(false);
      setRestoreTarget(null);
    },
  });

  // Helper function to check if content contains links
  // Detects: URLs, Markdown links, bare domains, file paths, HTML links, and auto-linked URLs in rich content
  const hasLinks = (content: string, richContent?: string): boolean => {
    if (!content && !richContent) return false;

    // Combine both content sources for comprehensive link detection
    const textToCheck = [content, richContent].filter(Boolean).join(" ");

    // Pattern for HTTP/HTTPS URLs (including those in rich text)
    const urlPattern = /https?:\/\/[^\s<>"]+/gi;

    // Pattern for Markdown links: [text](url) or [text](path)
    const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/gi;

    // Pattern for bare URLs without protocol (like example.com, domain.org)
    const bareUrlPattern = /\b[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s<>"]*)?/gi;

    // Pattern for relative/absolute paths that look like links
    const pathPattern = /(?:\.\.?\/|\/)[^\s<>"]+(?:\.[a-zA-Z]{2,}|\/)/gi;

    // Pattern for HTML links in rich content (href attributes)
    const htmlLinkPattern = /href=["']([^"']+)["']/gi;

    // Pattern for auto-linked URLs in rich text (wrapped in <a> tags)
    const autoLinkPattern = /<a[^>]*>([^<]+)<\/a>/gi;

    return (
      urlPattern.test(textToCheck) ||
      markdownLinkPattern.test(textToCheck) ||
      bareUrlPattern.test(textToCheck) ||
      pathPattern.test(textToCheck) ||
      htmlLinkPattern.test(textToCheck) ||
      autoLinkPattern.test(textToCheck)
    );
  };
  // Enhanced comment state
  const [newComment, setNewComment] = useState("");
  const [newRichComment, setNewRichComment] = useState("");
  const [commentAttachments, setCommentAttachments] = useState<
    AttachmentFile[]
  >([]);
  const [commentMentions, setCommentMentions] = useState<string[]>([]);
  const [localComments, setLocalComments] = useState<CommentData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetFileUpload, setResetFileUpload] = useState(0); // Add reset trigger
  // Filter and search state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [selectedCommentMember, setSelectedCommentMember] =
    useState<string>("all");
  const [selectedCommentType, setSelectedCommentType] = useState<string>("all");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);

  // State for expanded changes view
  const [expandedChanges, setExpandedChanges] = useState<Set<string>>(
    new Set()
  );
  const [showPreviousValues, setShowPreviousValues] = useState<Set<string>>(
    new Set()
  );

  // State for hover highlighting on thread lines (Reddit-style)
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);

  // State for collapsed threads (Reddit-style)
  const [collapsedThreads, setCollapsedThreads] = useState<Set<string>>(
    new Set()
  );

  const toggleThreadCollapse = (threadId: string) => {
    setCollapsedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  };

  const commentEditorRef = useRef<RichTextEditorRef>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedCommentId = searchParams.get("commentId");

  // Always use project comments as the source of truth, with local optimistic updates
  useEffect(() => {
    setLocalComments(project?.comments || []);
  }, [project?.comments]);

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case Roles.ADMIN:
        return "destructive" as const;
      case Roles.VENDOR:
        return "default" as const;
      case Roles.CONTRACTOR:
        return "secondary" as const;
      case Roles.CLIENT:
      default:
        return "outline" as const;
    }
  };
  const getUserDisplayName = (
    user: {
      firstName?: string;
      lastName?: string;
      name?: string;
      email?: string;
    } | null
  ): string => {
    if (!user) return "System";
    // Only use firstName/lastName if at least one is a non-empty string
    const firstName = user.firstName ? user.firstName.trim() : "";
    const lastName = user.lastName ? user.lastName.trim() : "";

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return user.name || user.email || "Unknown User";
  };

  // Scroll to highlighted comment if present
  useEffect(() => {
    if (highlightedCommentId && localComments.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        // Try multiple possible selectors for the comment
        const selectors = [
          `#comment-${highlightedCommentId}`, // EnhancedComment ID
          `[data-comment-id="${highlightedCommentId}"]`, // Data attribute
          `[id*="${highlightedCommentId}"]`, // Any element with ID containing the comment ID
        ];

        let commentElement: HTMLElement | null = null;

        for (const selector of selectors) {
          commentElement = document.querySelector(selector);
          if (commentElement) break;
        }

        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Add highlight effect
          commentElement.classList.add("highlighted-comment");

          // Remove commentId from URL after scroll
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("commentId");
          setTimeout(() => {
            setSearchParams(newParams, { replace: true });
            commentElement?.classList.remove("highlighted-comment");
          }, 3000);
        } else {
          console.warn(
            "Comment element not found for ID:",
            highlightedCommentId
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [highlightedCommentId, searchParams, setSearchParams, localComments]);

  // Get project members for mentions
  const projectMembers = useMemo(() => {
    const members = [...(project.members || [])];

    // Add project creator if not already in members
    if (
      project.createdBy &&
      !members.find((m) => m._id === project.createdBy._id)
    ) {
      members.push(project.createdBy);
    }

    return members.map((member) => {
      const firstName =
        member.firstName && member.firstName !== "undefined"
          ? member.firstName.trim()
          : "";
      const lastName =
        member.lastName && member.lastName !== "undefined"
          ? member.lastName.trim()
          : "";
      const fullName =
        firstName || lastName ? `${firstName} ${lastName}`.trim() : "";

      return {
        _id: member._id,
        fullName: fullName || member.email || "Unknown User",
        email: member.email || "",
      };
    });
  }, [project.members, project.createdBy]);

  // Compose timeline entries with improved data processing including comments
  const timeline: TimelineEntry[] = useMemo(() => {
    const entries: TimelineEntry[] = [];

    // Separate comments into activity-linked and standalone
    const activityComments = new Map<string, CommentData[]>();
    const standaloneComments: CommentData[] = [];

    localComments.forEach((comment) => {
      if (comment.parentActivityId) {
        const existing = activityComments.get(comment.parentActivityId) || [];
        existing.push(comment);
        activityComments.set(comment.parentActivityId, existing);
      } else if (!comment.parentComment) {
        standaloneComments.push(comment);
      }
    });

    // Project creation
    entries.push({
      _id: "creation",
      type: "creation",
      timestamp:
        project.createdAt instanceof Date
          ? project.createdAt.toISOString()
          : String(project.createdAt),
      user: project.createdBy,
      content: `Project "${project.name}" was created`,
      meta: { action: "creation" },
      replies: activityComments.get("creation") || [],
    });

    // Status changes from statusHistory
    const statusHistoryArr = Array.isArray(statusHistory) ? statusHistory : [];
    statusHistoryArr.forEach((h, idx) => {
      // Determine entry type and content based on meta action
      let entryType: TimelineEntry["type"] = "status";
      let content: React.ReactElement | string = "";
      const entryId = `${h.meta?.action || "status"}-${idx}-${h.changedAt}`;

      if (h.meta?.action) {
        const action = h.meta.action;

        // Fix for 'includes' and 'replace' errors
        if (
          typeof h.meta?.action === "string" &&
          h.meta.action.includes("member_")
        ) {
          entryType = "member";
          const memberName = h.meta?.memberName || h.meta?.name || "A user";
          const role = h.meta?.role ? ` as ${h.meta.role}` : "";

          switch (h.meta.action) {
            case "member_added":
              content = (
                <>{`${memberName} was invited to the project${role}`}</>
              );
              break;
            case "member_joined":
              content = `Joined the project${role}`;
              break;
            case "member_removed":
              content = `${memberName} was removed from the project`;
              break;
            case "member_left":
              content = `Left the project`;
              break;
            default:
              content = `Member ${h.meta.action.replace("member_", "")}`;
          }
        } else if (
          typeof action === "string" &&
          action.includes("submission_")
        ) {
          entryType = "submission";
          switch (action) {
            case "submission_created":
              content = `Created a submission`;
              break;
            case "submission_edited": {
              // Use the changes summary if available, otherwise show generic message
              let submissionChangesSummary =
                h.meta?.changesSummary || "updated the submission";

              // Filter out unchanged fields from the summary
              if (
                h.meta?.detailedChanges &&
                Array.isArray(h.meta.detailedChanges)
              ) {
                const validChanges = h.meta.detailedChanges.filter(
                  (c: DetailedChange) => c.oldValue !== c.newValue
                );

                if (validChanges.length > 0) {
                  submissionChangesSummary = `updated submission: ${validChanges
                    .map((c: DetailedChange) => c.label)
                    .join(", ")}`;
                }
              }

              content = `${submissionChangesSummary}`;
              break;
            }
            case "submission_restored": {
              const restoredFields = h.meta?.restoredFields || 0;
              const restoredFromId = h.meta?.restoredFrom;
              const restoredFieldNames = h.meta?.restoredFieldNames as
                | string[]
                | undefined;

              // Find the original checkpoint entry to get its timestamp
              let checkpointInfo = "";
              if (restoredFromId && typeof restoredFromId === "string") {
                const parts = restoredFromId.split("-");
                if (parts.length >= 3) {
                  const timestampStr = parts.slice(2).join("-");
                  try {
                    const checkpointDate = new Date(timestampStr);
                    checkpointInfo = ` from ${formatDistanceToNow(
                      checkpointDate,
                      { addSuffix: true }
                    )} checkpoint`;
                  } catch {
                    checkpointInfo = " from a previous checkpoint";
                  }
                }
              }

              // Show field names if only specific fields were restored
              const fieldInfo =
                restoredFieldNames && restoredFieldNames.length <= 3
                  ? ` (${restoredFieldNames.join(", ")})`
                  : restoredFieldNames && restoredFieldNames.length > 3
                  ? ` (${restoredFieldNames.slice(0, 3).join(", ")} and ${
                      restoredFieldNames.length - 3
                    } more)`
                  : "";

              content = `Restored ${restoredFields} field(s)${fieldInfo}${checkpointInfo}`;
              break;
            }
            default:
              content = `${action
                .replace("submission_", "")
                .replace("_", " ")} submission`;
          }
        } else if (action === "project_updated") {
          entryType = "project_update"; // Use dedicated type for project updates

          // Use the changes summary if available, otherwise show generic message
          let changesSummary =
            h.meta?.changesSummary || "updated project details";

          // Filter out unchanged fields from the summary
          if (
            h.meta?.detailedChanges &&
            Array.isArray(h.meta.detailedChanges)
          ) {
            const validChanges = h.meta.detailedChanges.filter(
              (c: DetailedChange) => c.oldValue !== c.newValue
            );

            if (validChanges.length > 0) {
              changesSummary = `updated project details: ${validChanges
                .map((c: DetailedChange) => c.label)
                .join(", ")}`;
            }
          }

          content = `${changesSummary}`;
        } else if (action === "comment_added") {
          entryType = "comment";
          content = `Added a comment`;
        } else if (action === "comment_deleted") {
          entryType = "comment";
          content = `Deleted a comment`;
        } else {
          // Regular status change - show who made the change
          content = h.prevStatus
            ? `Changed status from ${h.prevStatus} to ${h.status}`
            : `Set status to ${h.status}`;
        }
      } else {
        // Fallback for status changes without meta - show who made the change
        content = h.prevStatus
          ? `Changed status from ${h.prevStatus} to ${h.status}`
          : `Set status to ${h.status}`;
      }

      // Fix for 'Date' type error
      // Ensure 'action' is always a string in 'meta'
      entries.push({
        _id: entryId,
        type: entryType,
        timestamp:
          h.changedAt instanceof Date
            ? h.changedAt.toISOString()
            : String(h.changedAt),
        user: h.changedBy,
        status: h.status,
        prevStatus: h.prevStatus,
        content,
        meta: {
          ...h.meta,
          action:
            typeof h.meta?.action === "string"
              ? h.meta.action
              : "status_change",
        },
        replies: activityComments.get(entryId) || [],
      });
    });

    // Add comments as timeline entries (top-level only, replies are handled within EnhancedComment)
    standaloneComments.forEach((comment) => {
      // Fix for 'Date' type error
      entries.push({
        _id: `comment-${comment._id}`,
        type: "comment",
        timestamp:
          comment.createdAt instanceof Date
            ? comment.createdAt.toISOString()
            : String(comment.createdAt),
        user: typeof comment.createdBy === "object" ? comment.createdBy : null,
        content: "comment", // Special marker for actual comment content
        meta: { action: "comment_content", commentData: comment },
      });
    });

    // Sort by timestamp descending (newest first)
    return entries.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [project, localComments, statusHistory]);

  // Filter options for timeline entries
  const filterOptions = [
    { value: "all", label: "All Activity", count: timeline.length },
    {
      value: "comment",
      label: "Comments",
      count: timeline.filter((e) => e.type === "comment").length,
    },
    {
      value: "status",
      label: "Status Changes",
      count: timeline.filter((e) => e.type === "status").length,
    },
    {
      value: "project_update",
      label: "Project Updates",
      count: timeline.filter((e) => e.type === "project_update").length,
    },
    {
      value: "member",
      label: "Member Activity",
      count: timeline.filter((e) => e.type === "member").length,
    },
    {
      value: "submission",
      label: "Submissions",
      count: timeline.filter((e) => e.type === "submission").length,
    },
    {
      value: "creation",
      label: "Creation",
      count: timeline.filter((e) => e.type === "creation").length,
    },
  ].filter((option) => option.count > 0); // Only show options with content
  // Get members who have made comments (including replies)
  const commentMembers = useMemo(() => {
    const members = new Map();

    // Helper function to process comments recursively (including replies)
    const processComment = (comment: CommentData) => {
      if (comment.createdBy && typeof comment.createdBy === "object") {
        const member = comment.createdBy;
        if (!members.has(member._id)) {
          const firstName =
            member.firstName && member.firstName !== "undefined"
              ? member.firstName.trim()
              : "";
          const lastName =
            member.lastName && member.lastName !== "undefined"
              ? member.lastName.trim()
              : "";
          const fullName =
            firstName || lastName ? `${firstName} ${lastName}`.trim() : "";

          members.set(member._id, {
            _id: member._id,
            fullName: fullName || member.email || "Unknown User",
            count: 0,
          });
        }
        members.get(member._id).count++;
      }

      // Process replies recursively
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => processComment(reply));
      }
    };
    // Helper function to count comments recursively
    const countCommentsRecursively = (comments: CommentData[]): number => {
      let count = 0;
      comments.forEach((comment) => {
        count++; // Count the comment itself
        if (comment.replies && comment.replies.length > 0) {
          count += countCommentsRecursively(comment.replies);
        }
      });
      return count;
    };

    localComments.forEach((comment) => processComment(comment));

    const totalCommentCount = countCommentsRecursively(localComments);

    return [
      { _id: "all", fullName: "All Members", count: totalCommentCount },
      ...Array.from(members.values()).sort((a, b) =>
        a.fullName.localeCompare(b.fullName)
      ),
    ];
  }, [localComments]);
  // Comment type filters (attachments, mentions, links) - including replies
  const commentTypeFilters = useMemo(() => {
    // Helper function to check comments recursively (including replies)
    const checkCommentRecursively = (
      comment: CommentData,
      checkFn: (c: CommentData) => boolean
    ): boolean => {
      if (checkFn(comment)) return true;
      if (comment.replies && comment.replies.length > 0) {
        return comment.replies.some((reply) =>
          checkCommentRecursively(reply, checkFn)
        );
      }
      return false;
    };

    // Helper function to count comments recursively
    const countCommentsRecursively = (comments: CommentData[]): number => {
      let count = 0;
      comments.forEach((comment) => {
        count++; // Count the comment itself
        if (comment.replies && comment.replies.length > 0) {
          count += countCommentsRecursively(comment.replies);
        }
      });
      return count;
    };

    const totalCommentCount = countCommentsRecursively(localComments);

    const types = [
      {
        value: "all",
        label: "All Comments",
        count: totalCommentCount,
      },
      {
        value: "attachments",
        label: "With Attachments",
        count: localComments.filter((c) =>
          checkCommentRecursively(
            c,
            (comment) => comment.attachments && comment.attachments.length > 0
          )
        ).length,
      },
      {
        value: "mentions",
        label: "With Mentions",
        count: localComments.filter((c) =>
          checkCommentRecursively(
            c,
            (comment) => comment.mentions && comment.mentions.length > 0
          )
        ).length,
      },
      {
        value: "links",
        label: "With Links/URLs",
        count: localComments.filter((c) =>
          checkCommentRecursively(c, (comment) =>
            hasLinks(comment.content || "", comment.richContent || "")
          )
        ).length,
      },
    ];

    return types.filter((type) => type.count > 0);
  }, [localComments]); // Highlighted comment IDs for search matches (both top-level and replies)
  const highlightedSearchCommentIds = useMemo(() => {
    const highlightedIds = new Set<string>();

    if (!searchQuery.trim()) {
      return highlightedIds;
    }

    const query = searchQuery.toLowerCase();

    // Helper function to recursively search all comments and replies
    const collectMatchingComments = (comment: CommentData) => {
      // Check if this comment matches the search query
      const contentMatch = comment.content?.toLowerCase().includes(query);
      const richContentMatch = comment.richContent
        ?.toLowerCase()
        .includes(query);
      const authorMatch =
        typeof comment.createdBy === "object" &&
        comment.createdBy &&
        (() => {
          const firstName =
            comment.createdBy.firstName &&
            comment.createdBy.firstName !== "undefined"
              ? comment.createdBy.firstName.trim()
              : "";
          const lastName =
            comment.createdBy.lastName &&
            comment.createdBy.lastName !== "undefined"
              ? comment.createdBy.lastName.trim()
              : "";
          const fullName =
            firstName || lastName ? `${firstName} ${lastName}`.trim() : "";

          return (
            fullName.toLowerCase().includes(query) ||
            comment.createdBy.email?.toLowerCase().includes(query)
          );
        })();

      if (contentMatch || richContentMatch || authorMatch) {
        highlightedIds.add(comment._id);
      }

      // Recursively search in replies
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => collectMatchingComments(reply));
      }
    };

    // Search through all comments
    localComments.forEach((comment) => {
      collectMatchingComments(comment);
    });

    return highlightedIds;
  }, [localComments, searchQuery]);

  // Highlighted comment IDs for member filter matches (both top-level and replies)
  const highlightedMemberCommentIds = useMemo(() => {
    const highlightedIds = new Set<string>();

    if (selectedFilter !== "comment" || selectedCommentMember === "all") {
      return highlightedIds;
    }

    // Helper function to recursively search all comments and replies by member
    const collectMemberComments = (comment: CommentData) => {
      // Check if this comment is by the selected member
      if (
        comment.createdBy &&
        typeof comment.createdBy === "object" &&
        comment.createdBy._id === selectedCommentMember
      ) {
        highlightedIds.add(comment._id);
      }

      // Recursively search in replies
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => collectMemberComments(reply));
      }
    };

    // Search through all comments
    localComments.forEach((comment) => {
      collectMemberComments(comment);
    });

    return highlightedIds;
  }, [localComments, selectedFilter, selectedCommentMember]);

  // Highlighted comment IDs for comment type filter matches (both top-level and replies)
  const highlightedTypeCommentIds = useMemo(() => {
    const highlightedIds = new Set<string>();

    if (selectedFilter !== "comment" || selectedCommentType === "all") {
      return highlightedIds;
    }

    // Helper function to recursively search all comments and replies by type
    const collectTypeComments = (comment: CommentData) => {
      let matches = false;

      switch (selectedCommentType) {
        case "attachments":
          matches = comment.attachments && comment.attachments.length > 0;
          break;
        case "mentions":
          matches = comment.mentions && comment.mentions.length > 0;
          break;
        case "links":
          matches = hasLinks(comment.content || "", comment.richContent || "");
          break;
        default:
          matches = false;
      }

      if (matches) {
        highlightedIds.add(comment._id);
      }

      // Recursively search in replies
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => collectTypeComments(reply));
      }
    };

    // Search through all comments
    localComments.forEach((comment) => {
      collectTypeComments(comment);
    });

    return highlightedIds;
  }, [localComments, selectedFilter, selectedCommentType]);
  // Combined highlighted comment IDs from all active filters (both top-level and replies)
  const combinedHighlightedCommentIds = useMemo(() => {
    const combined = new Set<string>();

    // Add search highlights
    highlightedSearchCommentIds.forEach((id) => combined.add(id));

    // Add member filter highlights
    highlightedMemberCommentIds.forEach((id) => combined.add(id));

    // Add type filter highlights
    highlightedTypeCommentIds.forEach((id) => combined.add(id));

    return combined;
  }, [
    highlightedSearchCommentIds,
    highlightedMemberCommentIds,
    highlightedTypeCommentIds,
  ]);

  // Filtered and searched timeline entries
  const filteredTimeline = useMemo(() => {
    let filtered = timeline;

    // Apply type filter
    if (selectedFilter !== "all") {
      filtered = filtered.filter((entry) => entry.type === selectedFilter);
    } // Apply comment member filter (only for comment entries)
    if (selectedFilter === "comment" && selectedCommentMember !== "all") {
      filtered = filtered.filter((entry) => {
        if (entry.type === "comment" && entry.meta?.commentData) {
          const comment = entry.meta.commentData;

          // Helper function to check if member made this comment or any of its replies
          const checkMemberInThread = (c: CommentData): boolean => {
            // Check the comment itself
            if (
              c.createdBy &&
              typeof c.createdBy === "object" &&
              c.createdBy._id === selectedCommentMember
            ) {
              return true;
            }
            // Check replies recursively
            if (c.replies && c.replies.length > 0) {
              return c.replies.some((reply) => checkMemberInThread(reply));
            }
            return false;
          };

          return checkMemberInThread(comment);
        }
        return true; // Keep non-comment entries when comment filter is applied
      });
    }

    // Apply comment type filter (attachments, mentions, links)
    if (selectedFilter === "comment" && selectedCommentType !== "all") {
      filtered = filtered.filter((entry) => {
        if (entry.type === "comment" && entry.meta?.commentData) {
          const comment = entry.meta.commentData;

          // Helper function to check comment thread recursively
          const checkCommentThread = (c: CommentData): boolean => {
            let matches = false;

            switch (selectedCommentType) {
              case "attachments":
                matches = c.attachments && c.attachments.length > 0;
                break;
              case "mentions":
                matches = c.mentions && c.mentions.length > 0;
                break;
              case "links":
                matches = hasLinks(c.content || "", c.richContent || "");
                break;
              default:
                matches = true;
            }

            // If this comment matches, return true
            if (matches) return true;

            // Check replies recursively
            if (c.replies && c.replies.length > 0) {
              return c.replies.some((reply) => checkCommentThread(reply));
            }

            return false;
          };

          return checkCommentThread(comment);
        }
        return true; // Keep non-comment entries when comment filter is applied
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((entry) => {
        // Search in content
        const contentMatch =
          typeof entry.content === "string"
            ? entry.content.toLowerCase().includes(query)
            : false;

        // Search in user name
        const userMatch =
          `${entry.user?.firstName} ${entry.user?.lastName}`
            .trim()
            ?.toLowerCase()
            .includes(query) || false;

        // Search in status
        const statusMatch =
          entry.status?.toLowerCase().includes(query) || false; // Search in comment content for comment entries (including replies)
        const commentMatch =
          entry.meta?.commentData?.content?.toLowerCase().includes(query) ||
          false;

        // Search in rich comment content for comment entries (including replies)
        const richCommentMatch =
          entry.meta?.commentData?.richContent?.toLowerCase().includes(query) ||
          false;
        // Search in replies recursively
        const replyMatch = (() => {
          if (entry.type === "comment" && entry.meta?.commentData) {
            const searchInReplies = (comment: CommentData): boolean => {
              if (comment.replies && comment.replies.length > 0) {
                return comment.replies.some(
                  (reply) =>
                    reply.content?.toLowerCase().includes(query) ||
                    reply.richContent?.toLowerCase().includes(query) ||
                    (typeof reply.createdBy === "object" &&
                      `${reply.createdBy?.firstName} ${reply.createdBy?.lastName}`
                        .trim()
                        ?.toLowerCase()
                        .includes(query)) ||
                    searchInReplies(reply) // Recursive search in nested replies
                );
              }
              return false;
            };
            return searchInReplies(entry.meta.commentData);
          }
          return false;
        })();

        // Search in meta action
        const actionMatch =
          entry.meta?.action?.toLowerCase().includes(query) || false;

        return (
          contentMatch ||
          userMatch ||
          statusMatch ||
          commentMatch ||
          richCommentMatch ||
          replyMatch ||
          actionMatch
        );
      });
    }

    return filtered;
  }, [
    timeline,
    selectedFilter,
    selectedCommentMember,
    selectedCommentType,
    searchQuery,
  ]);

  // Clear filters function
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedFilter("all");
    setSelectedCommentMember("all");
    setSelectedCommentType("all");
  };

  // Reset attachment input after adding comment or reply
  const resetAttachmentInput = () => {
    setCommentAttachments([]);
    setResetFileUpload((prev) => prev + 1); // Trigger reset
  };

  const handleInlineReplySubmit = async () => {
    if (!activeReplyId || !inlineReplyContent.trim() || !user) return;

    try {
      await addComment(
        project._id,
        inlineReplyContent,
        inlineReplyRichContent,
        inlineReplyAttachments,
        inlineReplyMentions,
        undefined, // parentComment
        activeReplyId // parentActivityId
      );

      // Reset state
      setActiveReplyId(null);
      setInlineReplyContent("");
      setInlineReplyRichContent("");
      setInlineReplyAttachments([]);
      setInlineReplyMentions([]);
    } catch (error) {
      console.error("Failed to add inline reply:", error);
    }
  };

  // Enhanced comment submission handler
  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setIsSubmitting(true);
    try {
      // Submit to backend with enhanced features
      await addComment(
        project._id,
        newComment,
        newRichComment,
        commentAttachments,
        commentMentions
      );

      // Clear form
      setNewComment("");
      setNewRichComment("");
      resetAttachmentInput();
      setCommentMentions([]);
      resetAttachmentInput();
      commentEditorRef.current?.clear();
      setIsCommentExpanded(false); // Close the form on success
    } catch (error) {
      console.error("Failed to add comment:", error);
      // Optionally show error toast
    } finally {
      setIsSubmitting(false);
    }
  };
  // Enhanced reply handler
  const handleReply = async (
    parentId: string,
    content: string,
    richContent: string,
    attachments: File[],
    mentions: string[],
    targetUserName?: string // New optional parameter for mentioned user display
  ) => {
    try {
      // If targetUserName is provided, prepend mention to content for better UX
      let finalContent = content;
      let finalRichContent = richContent;

      if (targetUserName && !content.includes(`@${targetUserName}`)) {
        finalContent = `@${targetUserName} ${content}`;
        finalRichContent = `<span class="mention">@${targetUserName}</span> ${richContent}`;
      }

      await addComment(
        project._id,
        finalContent,
        finalRichContent,
        attachments,
        mentions,
        parentId
      );
      setNewComment("");
      setNewRichComment("");
      resetAttachmentInput();
      setCommentMentions([]);
      resetAttachmentInput();
    } catch (error) {
      console.error("Failed to add reply:", error);
    }
  };

  // Enhanced edit handler
  const handleEdit = async (
    commentId: string,
    content: string,
    richContent: string,
    attachments?: File[],
    replaceAttachments?: boolean
  ) => {
    try {
      await editComment(
        project._id,
        commentId,
        content,
        richContent,
        attachments,
        replaceAttachments
      );
    } catch (error) {
      console.error("Failed to edit comment:", error);
    }
  };

  // Enhanced delete handler
  const handleDelete = async (commentId: string) => {
    try {
      await removeComment(project._id, commentId);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  };

  // Mention handler
  const handleMention = (userId: string) => {
    if (!commentMentions.includes(userId)) {
      setCommentMentions([...commentMentions, userId]);
    }
  };
  const handleMentionSelect = (mention: { id: string; label: string }) => {
    handleMention(mention.id);
  };

  const mentionOptions = projectMembers
    .filter((member) => member._id !== user?._id) // Exclude current user from mentions
    .map((member) => ({
      id: member._id,
      label: member.fullName,
      email: member.email,
    }));

  // Restore previous values - show detailed view with copy buttons
  const handleRestorePreviousValues = (
    changes: DetailedChange[],
    entryType: string,
    entryId: string
  ) => {
    // Extract field names for individual field restoration
    const fieldNames = changes.map((c) => c.field);

    // Get total changes from the entry to determine if this is partial restore
    const entry = timeline.find((e) => e._id === entryId);
    const totalChanges = entry?.meta?.detailedChanges?.length || 0;
    const isPartialRestore = changes.length < totalChanges;

    // Open confirmation dialog
    setRestoreTarget({
      entryId,
      changeCount: changes.length,
      fieldNames: isPartialRestore ? fieldNames : undefined, // Only pass field names for partial restore
    });
    setRestoreDialogOpen(true);
  };

  // Confirm restore action
  const confirmRestore = () => {
    if (restoreTarget) {
      restoreMutation.mutate({
        statusHistoryId: restoreTarget.entryId,
        fieldsToRestore: restoreTarget.fieldNames,
      });
    }
  };

  // Helper functions for rendering
  const getTimelineIcon = (entry: TimelineEntry) => {
    switch (entry.type) {
      case "creation":
        return <UserCircle className="h-4 w-4" />;
      case "status": {
        if (
          entry.meta?.action &&
          actionIcons[entry.meta.action as keyof typeof actionIcons]
        ) {
          const ActionIcon =
            actionIcons[entry.meta.action as keyof typeof actionIcons];
          return <ActionIcon className="h-4 w-4" />;
        }
        const StatusIcon = entry.status
          ? statusIcons[entry.status] || Activity
          : Activity;
        return <StatusIcon className="h-4 w-4" />;
      }
      case "comment":
        // Check if this is a comment action from status history
        if (
          entry.meta?.action === "comment_added" ||
          entry.meta?.action === "comment_deleted"
        ) {
          const ActionIcon =
            actionIcons[entry.meta.action as keyof typeof actionIcons] ||
            MessageSquare;
          return <ActionIcon className="h-4 w-4" />;
        }
        return <MessageSquare className="h-4 w-4" />;
      case "member": {
        if (
          entry.meta?.action === "member_added" ||
          entry.meta?.action === "member_joined"
        ) {
          return <UserPlus className="h-4 w-4" />;
        }
        return <UserMinus className="h-4 w-4" />;
      }
      case "submission":
        return <FileText className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getTimelineIconVariant = (
    entry: TimelineEntry
  ): "default" | "success" | "warning" | "error" | "info" => {
    switch (entry.type) {
      case "creation":
        return "info";
      case "status":
        if (entry.status === ProjectStatus.COMPLETED) return "success";
        if (entry.status === ProjectStatus.REJECTED) return "error";
        if (entry.status === ProjectStatus.ON_HOLD) return "warning";
        return "default";
      case "comment":
        // Different variants for comment actions
        if (entry.meta?.action === "comment_deleted") return "warning";
        return "info";
      case "member":
        return entry.meta?.action?.includes("removed") ||
          entry.meta?.action?.includes("left")
          ? "warning"
          : "success";
      case "submission":
        return "info";
      default:
        return "default";
    }
  };

  const getUserInitials = (
    user: {
      firstName?: string;
      lastName?: string;
      name?: string;
      email?: string;
    } | null
  ) => {
    if (!user) return "SY"; // For System

    // Try firstName + lastName first
    if (user.firstName || user.lastName) {
      const first = user.firstName?.charAt(0).toUpperCase() || "";
      const last = user.lastName?.charAt(0).toUpperCase() || "";
      return first + last || "??";
    }

    // Try different possible name properties
    const name = user.name || "";
    if (!name) {
      // Fallback to first letter of email if available
      const email = user.email;
      return email ? email.charAt(0).toUpperCase() + "U" : "??";
    }

    return name
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  // Update Badge color to match timeline color with current status
  const getStatusBadgeVariant = (
    status: ProjectStatus
  ):
    | "default"
    | "success"
    | "warning"
    | "secondary"
    | "destructive"
    | "outline" => {
    switch (status) {
      case ProjectStatus.COMPLETED:
        return "success";
      case ProjectStatus.REJECTED:
        return "destructive"; // Map "error" to "destructive"
      case ProjectStatus.ON_HOLD:
        return "warning";
      case ProjectStatus.IN_PROGRESS:
        return "secondary"; // Map "info" to "secondary"
      case ProjectStatus.SUBMITTED:
        return "default";
      case ProjectStatus.PENDING:
        return "default";
      case ProjectStatus.DRAFT:
        return "default";
      default:
        return "default";
    }
  };
  // Main component render
  return (
    <div className="space-y-8 relative">
      {/* Header Section */}
      <div className="space-y-6 bg-card p-6 rounded-xl border border-border/40 shadow-sm backdrop-blur-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-border/40">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground/90">
            <div className="p-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary shadow-sm ring-1 ring-inset ring-primary/10">
              <Activity className="h-5 w-5" />
            </div>
            Activity History
          </h3>

          <div className="flex items-center gap-2">
            {/* Add Comment Button */}
            {user && !isCommentExpanded && (
              <Button
                size="sm"
                onClick={() => setIsCommentExpanded(true)}
                className="flex items-center gap-2 shadow-sm transition-all duration-200"
              >
                <Plus className="h-4 w-4" />
                New Post
              </Button>
            )}

            {/* Filter Controls */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className={cn(
                "flex items-center gap-2 border-border/60 shadow-sm bg-background/50 backdrop-blur-sm hover:bg-accent hover:text-accent-foreground transition-all duration-200",
                isFilterExpanded &&
                  "bg-accent text-accent-foreground border-accent"
              )}
            >
              <Filter className="h-4 w-4" />
              Filters
              {filteredTimeline.length > 0 &&
                filteredTimeline.length !== timeline.length && (
                  <Badge
                    variant="secondary"
                    className="ml-1 h-5 px-1.5 text-xs"
                  >
                    {filteredTimeline.length}
                  </Badge>
                )}
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        {isFilterExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg border border-border/40 animate-in fade-in slide-in-from-top-2">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search activity..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 bg-background/80"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Type
              </label>
              <Select value={selectedFilter} onValueChange={setSelectedFilter}>
                <SelectTrigger className="h-9 bg-background/80">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="flex items-center justify-between w-full gap-2">
                        {option.label}
                        <Badge
                          variant="secondary"
                          className="text-[10px] h-4 px-1"
                        >
                          {option.count}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Member
              </label>
              <Select
                value={selectedCommentMember}
                onValueChange={setSelectedCommentMember}
                disabled={
                  selectedFilter !== "comment" && selectedFilter !== "all"
                }
              >
                <SelectTrigger className="h-9 bg-background/80">
                  <SelectValue placeholder="Filter by member" />
                </SelectTrigger>
                <SelectContent>
                  {commentMembers.map((member) => (
                    <SelectItem key={member._id} value={member._id}>
                      <span className="flex items-center justify-between w-full gap-2">
                        {member.fullName}
                        <Badge
                          variant="secondary"
                          className="text-[10px] h-4 px-1"
                        >
                          {member.count}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Content
              </label>
              <Select
                value={selectedCommentType}
                onValueChange={setSelectedCommentType}
                disabled={
                  selectedFilter !== "comment" && selectedFilter !== "all"
                }
              >
                <SelectTrigger className="h-9 bg-background/80">
                  <SelectValue placeholder="Filter by content" />
                </SelectTrigger>
                <SelectContent>
                  {commentTypeFilters.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center justify-between w-full gap-2">
                        {type.label}
                        <Badge
                          variant="secondary"
                          className="text-[10px] h-4 px-1"
                        >
                          {type.count}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-4 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs h-8 hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        )}

        {/* Global Post Input (Top Level) */}
        {user && isCommentExpanded && (
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="relative bg-card border border-primary/20 rounded-xl shadow-lg p-4 ring-1 ring-primary/5">
              <div className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-full shadow-sm flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                New Post
              </div>

              <RichTextEditor
                content={newRichComment}
                onChange={(text, html) => {
                  setNewComment(text);
                  setNewRichComment(html);
                }}
                mentions={mentionOptions}
                onMentionSelect={handleMentionSelect}
                placeholder="Share an update, ask a question, or post a comment..."
                className="min-h-[120px] bg-background/50 shadow-inner border-border/60 focus-within:ring-2 focus-within:ring-primary/20 transition-all rounded-lg mt-2"
              />

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/40">
                <CompactFileUpload
                  onFilesChange={setCommentAttachments}
                  maxFiles={5}
                  maxSize={10 * 1024 * 1024}
                  resetTrigger={resetFileUpload}
                />
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsCommentExpanded(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCommentSubmit}
                    disabled={!newComment.trim() || isSubmitting}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all px-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Activity className="mr-2 h-4 w-4 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Post Update
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- TIMELINE RENDERING --- */}
        {filteredTimeline.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No activity found matching your filters.</p>
            <Button
              variant="link"
              onClick={() => {
                setSelectedFilter("all");
                setSelectedCommentMember("all");
                setSearchQuery("");
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredTimeline.map((entry, index) => {
              const isHighlighted = highlightedCommentId === entry._id;
              const isComment = entry.type === "comment";

              // Check if thread is active
              const hasRepliesOrInput =
                (entry.replies && entry.replies.length > 0) ||
                activeReplyId === entry._id;
              const isCollapsed = collapsedThreads.has(entry._id);
              const isHovered = hoveredThreadId === entry._id;

              // 1. Standalone Top-Level Comments
              if (isComment && entry.meta?.commentData) {
                return (
                  <div
                    key={entry._id}
                    className={cn(
                      "animate-in fade-in slide-in-from-bottom-2",
                      isHighlighted && "bg-primary/5 rounded-lg -mx-2 px-2"
                    )}
                  >
                    <EnhancedComment
                      comment={entry.meta.commentData}
                      projectMembers={projectMembers}
                      onReply={handleReply}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onMention={handleMention}
                      isHighlighted={
                        highlightedCommentId === entry.meta.commentData._id ||
                        combinedHighlightedCommentIds.has(
                          entry.meta.commentData._id
                        )
                      }
                      highlightedCommentId={highlightedCommentId}
                      highlightedReplyIds={combinedHighlightedCommentIds}
                      hideHeader={false}
                    />
                  </div>
                );
              }

              // 2. System Events (Tree Structure)
              return (
                <div
                  key={entry._id}
                  className={cn(
                    "flex flex-row items-stretch animate-in fade-in slide-in-from-bottom-2 group relative mb-8",
                    isHighlighted && "bg-primary/5 -mx-4 px-4 py-2 rounded-lg"
                  )}
                >
                  {/* LEFT COLUMN: Parent Avatar & The "Trunk" */}
                  <div className="flex flex-col items-center w-14 shrink-0 relative mr-4">
                    {/* Connector Line */}
                    {index !== filteredTimeline.length - 1 && (
                      <div className="absolute top-12 -bottom-10 w-[2px] bg-border/50 left-1/2 -translate-x-1/2 z-0" />
                    )}

                    {/* Parent Icon/Avatar */}
                    <div
                      className={cn(
                        "flex items-center justify-center w-12 h-12 rounded-full shadow-md z-20 relative transition-transform duration-300 group-hover:scale-105 border-4 border-background",
                        entry.type === "status"
                          ? "bg-secondary text-secondary-foreground"
                          : entry.type === "creation"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {getTimelineIcon(entry)}
                    </div>
                  </div>

                  {/* RIGHT COLUMN: Content & Branching Replies */}
                  <div className="flex-1 min-w-0">
                    {/* Content Container */}
                    <div className="relative mb-2">
                      
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-foreground/90">
                          {getUserDisplayName(entry.user)}
                        </span>
                        {entry.user?.role && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] h-5 px-1.5 font-medium text-muted-foreground bg-muted/50 border-0"
                          >
                            {entry.user.role}
                          </Badge>
                        )}
                        <span className="text-xs font-medium text-muted-foreground/60">
                          •{" "}
                          {formatDistanceToNow(new Date(entry.timestamp), {
                            addSuffix: true,
                          })}
                        </span>
                        {hasRepliesOrInput && (
                          <button
                            onClick={() => toggleThreadCollapse(entry._id)}
                            className="text-muted-foreground/60 hover:text-primary transition-colors ml-1 p-0.5 rounded-full hover:bg-primary/10"
                          >
                            {isCollapsed ? (
                              <Plus className="h-3 w-3" />
                            ) : (
                              <Minus className="h-3 w-3" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Content */}
                      <div className="text-sm text-foreground/80 leading-relaxed mb-4">
                        {entry.content}
                        {entry.type === "status" && entry.status && (
                          <Badge
                            variant={getStatusBadgeVariant(entry.status)}
                            className="ml-2 text-[10px] h-5 px-2 shadow-sm"
                          >
                            {entry.status.replace("_", " ")}
                          </Badge>
                        )}
                      </div>

                      {/* Detailed Changes Block - Preserving existing logic */}
                      {(entry.type === "submission" ||
                        entry.type === "project_update") &&
                        entry.meta?.detailedChanges &&
                        entry.meta.detailedChanges.some(
                          (c) => c.oldValue !== c.newValue
                        ) &&
                        !isCollapsed && (
                          <div className="mb-4 rounded-lg border border-border/40 bg-muted/20 p-4 text-xs shadow-inner max-w-2xl">
                            <div className="flex items-center justify-between mb-3">
                              <div className="font-semibold text-muted-foreground/80 uppercase tracking-wider text-[10px]">
                                {entry.meta?.action === "submission_restored"
                                  ? "Restored Changes"
                                  : "Changes"}
                              </div>
                              {entry.meta?.action !== "submission_restored" &&
                                entry.meta.detailedChanges.some(
                                  (c) =>
                                    c.oldValue !== c.newValue &&
                                    !(
                                      c.oldValue === "previous file(s)" &&
                                      c.newValue === "new file(s)"
                                    )
                                ) && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 bg-background hover:bg-accent border-border/60 shadow-sm"
                                    onClick={() =>
                                      handleRestorePreviousValues(
                                        entry.meta.detailedChanges!,
                                        entry.type,
                                        entry._id
                                      )
                                    }
                                    title="Restore all previous values"
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1.5" />
                                    Restore Values
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 hover:bg-background/50"
                                    onClick={() => {
                                      setShowPreviousValues((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(entry._id)) {
                                          next.delete(entry._id);
                                        } else {
                                          next.add(entry._id);
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    {showPreviousValues.has(entry._id)
                                      ? "Hide"
                                      : "View"}{" "}
                                    Previous
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              {(() => {
                                const isExpanded = expandedChanges.has(
                                  entry._id
                                );
                                const showPrevious = showPreviousValues.has(
                                  entry._id
                                );
                                const validChanges = entry.meta.detailedChanges.filter(
                                  (c) => c.oldValue !== c.newValue
                                );
                                const changesToShow = isExpanded
                                  ? validChanges
                                  : validChanges.slice(0, 5);

                                return (
                                  <>
                                    {showPrevious ? (
                                      // Show previous values in a table format
                                      <div className="rounded-lg border border-border/60 bg-background/50 overflow-hidden shadow-sm">
                                        <div className="grid grid-cols-[1fr,auto,1fr,auto] gap-3 p-2.5 font-medium border-b border-border/60 bg-muted/30 text-muted-foreground">
                                          <div>Previous Value</div>
                                          <div></div>
                                          <div>New Value</div>
                                          <div></div>
                                        </div>
                                        {changesToShow.map(
                                          (
                                            change: DetailedChange,
                                            idx: number
                                          ) => (
                                            <div
                                              key={idx}
                                              className="grid grid-cols-[1fr,auto,1fr,auto] gap-3 p-2.5 border-b border-border/40 last:border-b-0 hover:bg-muted/20 transition-colors items-center"
                                            >
                                              <div className="flex flex-col gap-1.5">
                                                <div className="font-medium text-foreground/80">
                                                  {change.label}
                                                </div>
                                                <div className="relative group">
                                                  <div className="font-mono bg-muted/50 p-1.5 pr-8 rounded border border-border/40 break-all text-[10px] md:text-xs text-muted-foreground">
                                                    {change.oldValue}
                                                  </div>
                                                  {!(
                                                    change.oldValue ===
                                                      "previous file(s)" &&
                                                    change.newValue ===
                                                      "new file(s)"
                                                  ) && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="absolute right-0.5 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm shadow-sm"
                                                      onClick={() => {
                                                        navigator.clipboard.writeText(
                                                          change.oldValue
                                                        );
                                                        toast({
                                                          title: "Copied",
                                                          description: `"${change.label}" previous value copied to clipboard`,
                                                          duration: 2000,
                                                        });
                                                      }}
                                                      title="Copy this value"
                                                    >
                                                      <Copy className="h-3 w-3" />
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center text-muted-foreground/40">
                                                →
                                              </div>
                                              <div className="flex flex-col gap-1.5">
                                                <div className="font-medium text-foreground/80">
                                                  {change.label}
                                                </div>
                                                <div className="font-mono bg-primary/5 border border-primary/10 p-1.5 rounded break-all font-medium text-[10px] md:text-xs text-primary/90">
                                                  {change.newValue}
                                                </div>
                                              </div>
                                              <div className="flex items-center justify-center">
                                                {!(
                                                  change.oldValue ===
                                                    "previous file(s)" &&
                                                  change.newValue ===
                                                    "new file(s)"
                                                ) && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary rounded-full"
                                                    onClick={() =>
                                                      handleRestorePreviousValues(
                                                        [change],
                                                        entry.type,
                                                        entry._id
                                                      )
                                                    }
                                                    title={`Restore "${change.label}" to previous value`}
                                                  >
                                                    <RotateCcw className="h-3.5 w-3.5" />
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      // Show compact diff format
                                      <div className="grid gap-2">
                                        {changesToShow.map(
                                          (
                                            change: DetailedChange,
                                            idx: number
                                          ) => (
                                            <div
                                              key={idx}
                                              className="flex items-center gap-2 flex-wrap bg-background/50 p-2 rounded border border-border/40 hover:border-border/80 transition-colors"
                                            >
                                              <span className="font-medium text-foreground/70">
                                                {change.label}:
                                              </span>
                                              {change.oldValue ===
                                                "previous file(s)" &&
                                              change.newValue ===
                                                "new file(s)" ? (
                                                <span className="font-medium bg-primary/10 px-1.5 py-0.5 rounded text-primary/90 border border-primary/10">
                                                  File has been updated
                                                </span>
                                              ) : (
                                                <>
                                                  <span className="text-muted-foreground line-through opacity-60 decoration-destructive/40 decoration-2">
                                                    {change.oldValue}
                                                  </span>
                                                  <span className="text-muted-foreground/40">
                                                    →
                                                  </span>
                                                  <span className="font-medium bg-primary/10 px-1.5 py-0.5 rounded text-primary/90 border border-primary/10">
                                                    {change.newValue}
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                    {validChanges.length > 5 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-auto p-0 text-xs text-primary hover:text-primary/80 hover:bg-transparent hover:underline mt-2 font-medium"
                                        onClick={() => {
                                          setExpandedChanges((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(entry._id)) {
                                              next.delete(entry._id);
                                            } else {
                                              next.add(entry._id);
                                            }
                                            return next;
                                          });
                                        }}
                                      >
                                        {isExpanded ? (
                                          <>Show less</>
                                        ) : (
                                          <>
                                            + {validChanges.length - 5} more
                                            change(s)
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                      {/* Reply Action */}
                      <div className="flex items-center gap-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/5 text-xs font-medium rounded-full transition-colors"
                          onClick={() =>
                            setActiveReplyId(
                              activeReplyId === entry._id ? null : entry._id
                            )
                          }
                        >
                          <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                          Reply
                        </Button>
                      </div>
                    </div>

                    {/* REPLIES TREE */}
                    {hasRepliesOrInput && !isCollapsed && (
                      <div className="relative space-y-4 pt-1">
                        {/* Existing Replies */}
                        {entry.replies?.map((reply) => (
                          <div key={reply._id} className="relative pl-8">
                            {/* CURVED CONNECTOR - REMOVED */}

                            <EnhancedComment
                              comment={reply}
                              projectMembers={projectMembers}
                              onReply={handleReply}
                              onEdit={handleEdit}
                              onDelete={handleDelete}
                              onMention={handleMention}
                              isHighlighted={false}
                              highlightedCommentId={null}
                              highlightedReplyIds={new Set()}
                              hideHeader={false}
                              isReply={true}
                            />
                          </div>
                        ))}

                        {/* Reply Editor */}
                        {activeReplyId === entry._id && (
                          <div className="relative pl-8 animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="relative bg-card border border-primary/20 rounded-xl shadow-lg p-4 ring-1 ring-primary/5">
                              <div className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                                <Reply className="h-3 w-3" />
                                Replying
                              </div>
                              <RichTextEditor
                                ref={inlineReplyEditorRef}
                                content={inlineReplyRichContent}
                                onChange={(text, html) => {
                                  setInlineReplyContent(text);
                                  setInlineReplyRichContent(html);
                                }}
                                mentions={mentionOptions}
                                onMentionSelect={handleMentionSelect}
                                placeholder="Write a reply..."
                                className="min-h-[100px] bg-background/50 shadow-inner border-border/60 focus-within:ring-2 focus-within:ring-primary/20 transition-all rounded-lg"
                              />
                              <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/40">
                                <CompactFileUpload
                                  onFilesChange={setInlineReplyAttachments}
                                  maxFiles={3}
                                  maxSize={5 * 1024 * 1024}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setActiveReplyId(null)}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all px-4"
                                    onClick={handleInlineReplySubmit}
                                  >
                                    Reply
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Collapsed Stub */}
                    {isCollapsed && hasRepliesOrInput && (
                      <button
                        onClick={() => toggleThreadCollapse(entry._id)}
                        className="text-xs text-muted-foreground hover:text-primary mt-1 italic flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />{" "}
                        {entry.replies?.length || 0} replies hidden
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Restore Dialog (Kept as is) */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Submission Values?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore {restoreTarget?.changeCount || 0} field(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              disabled={restoreMutation.isPending}
            >
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectTimelineTab;
